import { Button } from '@/components/ui/button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ComponentProps, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import ConfirmDialog from 'app/frontend/base/components/ConfirmDialog'
import { leaveTeam } from 'app/frontend/base/api/team'
import { usePageInfo } from 'app/frontend/base/config/queries'
import { displayErrorToast } from 'app/frontend/base/helpers'

type LeaveTeamButtonProps = {
  teamId: number
  teamPath?: string
  variant?: ComponentProps<typeof Button>['variant']
  size?: ComponentProps<typeof Button>['size']
  className?: string
}

const LeaveTeamButton = ({
  teamId,
  teamPath,
  variant = 'ghost',
  size = 'sm',
  className,
}: LeaveTeamButtonProps) => {
  const { t } = useTranslation()
  const {
    data: { user },
  } = usePageInfo()
  const queryClient = useQueryClient()
  const [confirmOpen, setConfirmOpen] = useState(false)

  const isOnTeam = user?.teamReferences.some(
    (teamReference) => teamReference.id === teamId,
  )

  const { mutateAsync: leave, isPending } = useMutation({
    mutationFn: () => leaveTeam(teamId),
    onSuccess: (newData) => {
      if (teamPath) {
        queryClient.setQueryData(['team', { path: teamPath }], newData)
      }
      queryClient.setQueryData<wowaudit.PageInfo>(['pageInfo'], (pageInfo) =>
        pageInfo?.user
          ? {
              ...pageInfo,
              user: {
                ...pageInfo.user,
                teamReferences: pageInfo.user.teamReferences.filter(
                  (teamReference) => teamReference.id !== teamId,
                ),
              },
            }
          : pageInfo,
      )
      toast.success(t('dashboard.team.roster.leave-team-success'))
    },
    onError: (error) => {
      displayErrorToast(error, t)
    },
  })

  if (!isOnTeam) {
    return null
  }

  return (
    <>
      <Button
        variant={variant}
        size={size}
        className={
          className ??
          (variant === 'ghost' ? 'text-muted-foreground' : undefined)
        }
        onClick={() => setConfirmOpen(true)}
      >
        {t('dashboard.team.roster.leave-team')}
      </Button>
      <ConfirmDialog
        open={confirmOpen}
        variant='destructive'
        title={t('dashboard.team.roster.leave-team-title')}
        description={t('dashboard.team.roster.leave-team-description')}
        continueText={t('dashboard.team.roster.leave-team')}
        loading={isPending}
        onCancel={() => setConfirmOpen(false)}
        onContinue={async () => {
          try {
            await leave()
            setConfirmOpen(false)
          } catch {
            // Error toast is already shown; keep the dialog open.
          }
        }}
      />
    </>
  )
}

export default LeaveTeamButton
